import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects'
import { ReturnCall } from '../types'
import { actions as appActions } from '../state'
import { PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  ApproveDisapprovePayload,
  CancelQuantityItemPayload,
  CreateInventoryPayload,
  DeleteBadgePayload,
  GetInventoryPayload,
  GetInventoryProductDetailsPayload,
  GetPositionPayload,
  IdBadgePayload,
  IdPayload,
  IncrementDecrementPayload,
  InventoriesToExecutePayload,
  InventoryStatus,
  ResetAdjustmentApprovalPayload,
  SavePositionDamagePayload,
  SavePositionPayload,
  UnlinkOperatorPayload,
  ValidateBadgeExecuteInventoryPayload,
  ValidateBadgePayload,
  ValidateSkusPayload,
} from './InventoryTypes'
import apis from './InventoryApi'
import { actions, initialState } from './InventoryState'
import { selectBadges, selectDataPosition } from './InventorySelector'

export function* getInventory(
  data: PayloadAction<GetInventoryPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getInventory> = yield call(
      apis.getInventory,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar a lista de inventários!')
      yield put(actions.setInventoryList({ inventoryList: initialState.inventoryList }))
      return
    }

    yield put(actions.setInventoryList({ inventoryList: result.data }))
  } catch (err: any) {
    toast.error('Falha ao buscar a lista de inventários!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* createInventory(
  data: PayloadAction<CreateInventoryPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.createInventory> = yield call(
      apis.createInventory,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao criar o inventário!')
      return
    }

    toast.success('Inventário criado com sucesso!')
    yield put(actions.clearState())
  } catch (err: any) {
    toast.error('Falha ao criar o inventário!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateSkus(
  data: PayloadAction<ValidateSkusPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.validateSkus> = yield call(
      apis.validateSkus,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao validar SKUs!')
      return
    }

    yield put(actions.setSkusList({ skusList: result.data }))
  } catch (err: any) {
    toast.error('Falha ao validar SKUs!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateAndAddBadge({ payload }: PayloadAction<ValidateBadgePayload>) {
  const { badge, setError, onSuccess } = payload
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.validateAndAddBadge> = yield call(
      apis.validateAndAddBadge,
      badge,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Crachá inválido ou já habilitado!')
      setError('Crachá inválido')
      return
    }

    const responseToAdd = [response.data]

    yield put(actions.setBadgeAdd({ badge: responseToAdd }))
    yield put(actions.getBadges())
    onSuccess()
  } catch (error: any) {
    setError('Falha ao validar o crachá!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* deleteBadge(payload: PayloadAction<DeleteBadgePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.deleteBadge> = yield call(
      apis.deleteBadge,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao deletar o crachá!')
      return
    }

    toast.success('Crachá deletado com sucesso!')
    const currentBadges = yield select(selectBadges)
    const newBadges = currentBadges.filter((item) => item.userBadge !== payload.payload.badgeToDelete)
    yield put(actions.setBadge(newBadges))
    yield put(actions.getBadges())
  } catch (error: any) {
    toast.error('Falha ao deletar o crachá!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getBadges() {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.getBadges> = yield call(
      apis.getBadges,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao buscar a lista de crachás!')
      return
    }

    yield put(actions.setBadge({ badge: response.data }))
  } catch (error: any) {
    toast.error('Falha ao buscar a lista de crachás!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getInventoryDetail(payload: PayloadAction<IdPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.getInventoryDetail> = yield call(
      apis.getInventoryDetail,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao buscar a os detalhes do inventário!')
      return
    }

    yield put(actions.setInventoryDetail({ inventoryDetails: response.data }))
  } catch (error: any) {
    toast.error('Falha ao buscar a os detalhes do inventário!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getInventoryProductDetails(payload: PayloadAction<GetInventoryProductDetailsPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.getInventoryProductDetails> = yield call(
      apis.getInventoryProductDetails,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao buscar os detalhes do produto!')
      return
    }

    yield put(actions.setInventoryProductDetails(response.data))
  } catch (error: any) {
    toast.error('Falha ao buscar os detalhes do produto!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* cancelInventory(payload: PayloadAction<IdPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.cancelInventory> = yield call(
      apis.cancelInventory,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao cancelar o inventário!')
      return
    }

    toast.success('Inventário cancelado com sucesso!')
    yield put(actions.setInventoryStatus({ id: Number(payload.payload), currentStatus: InventoryStatus.CANCELED }))
  } catch (error: any) {
    toast.error('Falha ao cancelar o inventário!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getInventoriesToExecute(payload: PayloadAction<InventoriesToExecutePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.getInventoriesToExecute> = yield call(
      apis.getInventoriesToExecute,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao buscar a lista de inventários!')
      return
    }

    yield put(actions.setInventoriesToExecute({ inventoriesToExecute: response.data }))
  } catch (error: any) {
    toast.error('Falha ao buscar a lista de inventários!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getConfigsToExecuteInventory() {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.getConfigsToExecuteInventory> = yield call(
      apis.getConfigsToExecuteInventory,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao buscar as configurações do inventário!')
      return
    }

    yield put(actions.setConfigsToExecuteInventory(response.data))
  } catch (error: any) {
    toast.error('Falha ao buscar as configurações do inventário!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* validateBadgeToExecuteInventory({ payload }: PayloadAction<ValidateBadgeExecuteInventoryPayload>) {
  const {
    badge, id, setError,
  } = payload
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.validateBadgeToExecuteInventory> = yield call(
      apis.validateBadgeToExecuteInventory,
      badge, id,
    )

    if (!response.isOk) {
      setError('Crachá inválido')
      toast.error(response?.data?.data?.message || 'Falha ao validar o crachá do supervisor e ao buscar atual posição!')
      return
    }

    yield put(actions.setDataToExecuteInventory({ badge }))

    const dataPosition = response.data

    if (dataPosition.openCount) {
      toast.info('Continuando contagem na posição atual!')
    }

    yield put(actions.setDataPosition({ dataPosition }))
  } catch (error: any) {
    setError('Falha ao validar o crachá do supervisor!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* finishInventory(payload: PayloadAction<IdPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.finishInventory> = yield call(
      apis.finishInventory,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao finalizar o inventário!')
      return
    }

    toast.success('Inventário finalizado com sucesso!')
    yield put(actions.setInventoryStatus({ id: Number(payload.payload), currentStatus: InventoryStatus.FINISHED }))
  } catch (error: any) {
    toast.error('Falha ao finalizar o inventário!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* incrementDecrementInventory(payload: PayloadAction<IncrementDecrementPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const { setOpen, setProductsCodeNotFound, setInputValue } = payload.payload
    const response: ReturnCall<typeof apis.incrementDecrementInventory> = yield call(
      apis.incrementDecrementInventory,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao incrementar/decrementar o inventário!')
      return
    }

    if (response.data.notFoundProducts.length > 0) {
      const productsCodeNotFound = response.data.notFoundProducts
      setProductsCodeNotFound(productsCodeNotFound)
      toast.error('Existe(m) código(s) de produto(s) não encontrado(s). Confira e tente novamente')
    } else {
      setOpen(false)
      setInputValue('')
      setProductsCodeNotFound([])
      toast.success('Inventário incrementado/decrementado com sucesso!')
    }

    yield put(actions.setInventoryDetail({ inventoryDetails: response.data.inventoryDetails }))
  } catch (error: any) {
    toast.error('Falha ao incrementar/decrementar o inventário!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* approveAdjustment(payload: PayloadAction<ApproveDisapprovePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const { idProductInventory, approvalStatus } = payload.payload
    const response: ReturnCall<typeof apis.approveAdjustment> = yield call(
      apis.approveAdjustment,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao aprovar o ajuste!')
      return
    }

    toast.success('Ajuste aprovado com sucesso!')
    yield put(actions.setApprovalAdjustment({ idProductInventory, approvalStatus }))
  } catch (error: any) {
    toast.error('Falha ao aprovar o ajuste!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* rejectAdjustment(payload: PayloadAction<ApproveDisapprovePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const { idProductInventory, approvalStatus } = payload.payload
    const response: ReturnCall<typeof apis.rejectAdjustment> = yield call(
      apis.rejectAdjustment,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao rejeitar o ajuste!')
      return
    }

    toast.success('Ajuste rejeitado com sucesso!')
    yield put(actions.setApprovalAdjustment({ idProductInventory, approvalStatus }))
  } catch (error: any) {
    toast.error('Falha ao rejeitar o ajuste!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* resetAdjustmentApproval(payload: PayloadAction<ResetAdjustmentApprovalPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const { idProductInventory } = payload.payload
    const response: ReturnCall<typeof apis.resetAdjustmentApproval> = yield call(
      apis.resetAdjustmentApproval,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao excluir aprovação de ajuste!')
      return
    }

    toast.success('Aprovação excluída com sucesso!')
    yield put(actions.setApprovalAdjustment({ idProductInventory, approvalStatus: null }))
  } catch (error: any) {
    toast.error('Falha ao excluir aprovação de ajuste!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getPosition(payload: PayloadAction<GetPositionPayload>) {
  const { isThirdCount } = payload.payload
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.getPosition> = yield call(
      apis.getPosition,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao buscar a posição!')
      const dataPosition = yield select(selectDataPosition)
      yield put(actions.setDataPosition({
        dataPosition: {
          ...initialState.dataPosition,
          enabledThirdCount: dataPosition.enabledThirdCount,
        },
      }))
      yield put(actions.setGoToPosition(false))
      yield put(actions.setAfterFinishPosition(false))
      return
    }

    yield put(actions.setDataPosition({ dataPosition: response.data }))
    yield put(actions.setThirdCount(isThirdCount))
  } catch (error: any) {
    toast.error('Falha ao buscar a posição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* registerStartPosition(payload: PayloadAction<IdBadgePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.registerStartPosition> = yield call(
      apis.registerStartPosition,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao registrar a posição inicial!')
      return
    }
  } catch (error: any) {
    toast.error('Falha ao registrar a posição inicial!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* savePosition(payload: PayloadAction<SavePositionPayload>) {
  const { setHasPosition } = payload.payload
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.savePosition> = yield call(
      apis.savePosition,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao adiciona a quantidade!')
      return
    }

    setHasPosition(true)
    toast.success('Quantidade adicionada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao salvar a posição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* savePositionDamage(payload: PayloadAction<SavePositionDamagePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.savePositionDamage> = yield call(
      apis.savePositionDamage,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao adicionar a quantidade de avarias!')
      return
    }

    toast.success('Quantidade de avarias adicionada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao salvar a posição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* cancelQuantityItem(payload: PayloadAction<CancelQuantityItemPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.cancelQuantityItem> = yield call(
      apis.cancelQuantityItem,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao cancelar o item da lista!')
      return
    }

    toast.success('Item cancelado com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao cancelar a validação!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* finishPosition(payload: PayloadAction<IdBadgePayload>) {
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.finishPosition> = yield call(
      apis.finishPosition,
      payload.payload,
    )

    if (!response.isOk) {
      toast.error(response?.data?.data?.message || 'Falha ao finalizar a posição!')
      return
    }

    yield put(actions.setNeedGetPosition(true))
    toast.success('Posição finalizada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao finalizar a posição!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* unlinkOperator(payload: PayloadAction<UnlinkOperatorPayload>) {
  const { setError } = payload.payload
  yield put(appActions.isLoading(true))

  try {
    const response: ReturnCall<typeof apis.unlinkOperator> = yield call(
      apis.unlinkOperator,
      payload.payload,
    )

    if (!response.isOk) {
      setError('Crachá inválido')
      toast.error(response?.data?.data?.message || 'Falha ao desvincular o operador!')
      return
    }

    toast.success('Operador desvinculado com sucesso!')
    yield put(actions.setDataPosition({ dataPosition: initialState.dataPosition }))
    yield put(actions.clearState())
  } catch (error: any) {
    toast.error('Falha ao desvincular o operador!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getInventory.type, getInventory),
    takeLatest(actions.createInventory.type, createInventory),
    takeLatest(actions.validateSkus.type, validateSkus),
    takeLatest(actions.validateAndAddBadge.type, validateAndAddBadge),
    takeLatest(actions.deleteBadge.type, deleteBadge),
    takeLatest(actions.getBadges.type, getBadges),
    takeLatest(actions.getInventoryDetail.type, getInventoryDetail),
    takeLatest(actions.getInventoryProductDetails.type, getInventoryProductDetails),
    takeLatest(actions.cancelInventory.type, cancelInventory),
    takeLatest(actions.getInventoriesToExecute.type, getInventoriesToExecute),
    takeLatest(actions.getConfigsToExecuteInventory.type, getConfigsToExecuteInventory),
    takeLatest(actions.validateBadgeToExecuteInventory.type, validateBadgeToExecuteInventory),
    takeLatest(actions.finishInventory.type, finishInventory),
    takeLatest(actions.incrementDecrementInventory.type, incrementDecrementInventory),
    takeLatest(actions.approveAdjustment.type, approveAdjustment),
    takeLatest(actions.rejectAdjustment.type, rejectAdjustment),
    takeLatest(actions.resetAdjustmentApproval.type, resetAdjustmentApproval),
    takeLatest(actions.getPosition.type, getPosition),
    takeLatest(actions.registerStartPosition.type, registerStartPosition),
    takeLatest(actions.savePosition.type, savePosition),
    takeLatest(actions.savePositionDamage.type, savePositionDamage),
    takeLatest(actions.cancelQuantityItem.type, cancelQuantityItem),
    takeLatest(actions.finishPosition.type, finishPosition),
    takeLatest(actions.unlinkOperator.type, unlinkOperator),
  ])
}
